import React from "react";

/**
 * BrandedLoader — full-viewport black background centred on the
 * Ur Designs brand mark (white "U" glyph + orange dot supplied by
 * Calvin as `/brand/loading-logo.png`, served from the frontend
 * static public folder so it renders instantly, without waiting on
 * `/api/settings`).
 *
 * All animations are CSS-only (no framer-motion, no library):
 *   • whole loader fades + very subtly scales in (320ms ease-out)
 *   • the mark gently breathes (opacity 1→0.85→1, scale 1→1.03→1)
 *
 * `prefers-reduced-motion: reduce` disables both animations — a
 * static centred mark is rendered on the black background.
 *
 * Viewport height uses `100svh` so iOS Safari + Android Chrome
 * dynamic browser bars don't produce an oversized black area.
 */
export const BrandedLoader = ({ testid = "branded-loader" }) => {
  return (
    <div
      data-testid={testid}
      className="fixed inset-0 z-[9999] grid place-items-center bg-[#0F0F0F] ur-branded-loader-enter"
      style={{ minHeight: "100svh" }}
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">Loading Ur Designs</span>
      <img
        src="/brand/loading-logo.png"
        alt="Ur Designs"
        data-testid="branded-loader-mark"
        className="ur-branded-loader-mark w-[128px] h-[128px] sm:w-[160px] sm:h-[160px] select-none pointer-events-none"
        draggable={false}
      />
      <style>{`
        @keyframes ur-loader-fade-in {
          0%   { opacity: 0; transform: scale(0.97); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes ur-loader-mark-breathe {
          0%, 100% { opacity: 1;    transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.03); }
        }
        .ur-branded-loader-enter { animation: ur-loader-fade-in 320ms ease-out both; }
        .ur-branded-loader-mark {
          transform-origin: center;
          animation: ur-loader-mark-breathe 1800ms ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .ur-branded-loader-enter { animation: none; opacity: 1; }
          .ur-branded-loader-mark  { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default BrandedLoader;
